/**
 * LeftShield Portal - AI Simulation Review Page
 * Review AI-generated policy simulation recommendations.
 * Route: /ai-governance/simulations | Category: AI
 */
import React from 'react';
import {
  Title2,
  Title3,
  Body1,
  Body2,
  Caption1,
  Card,
  Badge,
  Button,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import {
  BeakerRegular,
  BrainCircuitRegular,
  ArrowLeftRegular,
  CheckmarkRegular,
  DismissRegular,
  ShieldCheckmarkRegular,
  CodeRegular,
  ServerRegular,
  PipelineRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { governanceTokens } from '../components/design-system';

// ---------------------------------------------------------------------------
// Local styles
// ---------------------------------------------------------------------------

const AI_COLOR = '#8852E0';

const useLocalStyles = makeStyles({
  simulationCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  impactGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  impactTile: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    alignItems: 'center',
    textAlign: 'center' as const,
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  actionRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
  },
});

// ---------------------------------------------------------------------------
// Demo data
// ---------------------------------------------------------------------------

interface Simulation {
  id: string;
  proposedPolicy: string;
  reasoning: string;
  sourceAgent: string;
  impactAnalysis: {
    affectedRepos: number;
    affectedPipelines: number;
    affectedServices: number;
  };
  predictedBlocks: number;
  predictedWarns: number;
  falsePositiveRisk: 'low' | 'medium' | 'high';
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

const SIMULATIONS: Simulation[] = [
  {
    id: 'sim-001',
    proposedPolicy: 'Block builds containing detected PII patterns in application logs',
    reasoning: 'Over the past 7 days, 14 builds shipped log statements containing email addresses and phone numbers. This creates compliance risk under GDPR Article 5(1)(f) and SOC2 CC6.5. Blocking at build time prevents PII leakage into production log aggregators.',
    sourceAgent: 'PolicyEngine',
    impactAnalysis: {
      affectedRepos: 8,
      affectedPipelines: 12,
      affectedServices: 5,
    },
    predictedBlocks: 23,
    predictedWarns: 41,
    falsePositiveRisk: 'medium',
    status: 'pending',
    createdAt: '2026-03-09T10:15:00Z',
  },
  {
    id: 'sim-002',
    proposedPolicy: 'Auto-remediate critical CVEs in staging via dependency bump PRs',
    reasoning: 'Analysis of the last 30 days shows an average of 4.2 days between critical CVE disclosure and manual remediation. Automated staging PRs would reduce this to under 2 hours. The FixPRAgent has a 94% success rate on dependency bumps with no regression in test suites.',
    sourceAgent: 'FixPRAgent',
    impactAnalysis: {
      affectedRepos: 22,
      affectedPipelines: 22,
      affectedServices: 18,
    },
    predictedBlocks: 0,
    predictedWarns: 8,
    falsePositiveRisk: 'low',
    status: 'pending',
    createdAt: '2026-03-09T08:42:00Z',
  },
  {
    id: 'sim-003',
    proposedPolicy: 'Enforce MFA verification on all CI/CD service account tokens',
    reasoning: 'Security audit identified 6 CI/CD service accounts using long-lived tokens without MFA-backed issuance. NIST SP 800-63B recommends AAL2 for privileged automation accounts. Enforcing MFA-backed token issuance aligns with Essential Eight Maturity Level 2.',
    sourceAgent: 'ComplianceAdvisor',
    impactAnalysis: {
      affectedRepos: 0,
      affectedPipelines: 31,
      affectedServices: 6,
    },
    predictedBlocks: 6,
    predictedWarns: 0,
    falsePositiveRisk: 'low',
    status: 'pending',
    createdAt: '2026-03-08T16:30:00Z',
  },
  {
    id: 'sim-004',
    proposedPolicy: 'Require SBOM generation for all container image builds',
    reasoning: 'Executive Order 14028 and emerging supply chain regulations require Software Bill of Materials for deployed software. Currently only 34% of container builds generate SBOMs. Adding a mandatory SBOM step would bring compliance to 100% and enable vulnerability correlation across the supply chain.',
    sourceAgent: 'ComplianceAdvisor',
    impactAnalysis: {
      affectedRepos: 15,
      affectedPipelines: 19,
      affectedServices: 15,
    },
    predictedBlocks: 0,
    predictedWarns: 19,
    falsePositiveRisk: 'low',
    status: 'approved',
    createdAt: '2026-03-07T11:20:00Z',
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const falsePositiveColor = (risk: string): 'success' | 'warning' | 'danger' => {
  switch (risk) {
    case 'low': return 'success';
    case 'medium': return 'warning';
    case 'high': return 'danger';
    default: return 'warning';
  }
};

const statusColor = (status: string): 'warning' | 'success' | 'danger' => {
  switch (status) {
    case 'approved': return 'success';
    case 'rejected': return 'danger';
    default: return 'warning';
  }
};

const statusLabel = (status: string): string => {
  switch (status) {
    case 'approved': return 'Approved';
    case 'rejected': return 'Rejected';
    default: return 'Pending Review';
  }
};

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

const pendingCount = SIMULATIONS.filter((s) => s.status === 'pending').length;
const approvedCount = SIMULATIONS.filter((s) => s.status === 'approved').length;
const rejectedCount = SIMULATIONS.filter((s) => s.status === 'rejected').length;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AISimulationReviewPage: React.FC = () => {
  const ps = usePageStyles();
  const ls = useLocalStyles();
  const navigate = useNavigate();

  return (
    <div className={ps.page}>
      <PageHeader
        title="AI Simulation Review"
        subtitle="Review AI-generated policy recommendations. Each simulation shows predicted impact before any changes are applied."
        icon={<BeakerRegular />}
        iconColor={AI_COLOR}
        actions={
          <Button
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={() => navigate('/ai-governance')}
          >
            Back to AI Governance
          </Button>
        }
      />

      {/* Summary */}
      <Card>
        <div className={ps.cardBody}>
          <div className={ps.sectionHeader}>
            <Title2>Simulation Queue</Title2>
            <Badge appearance="outline" color="informative">
              {SIMULATIONS.length} total
            </Badge>
          </div>

          <div className={ps.statGrid}>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Pending Review</Body2>
              <Title2 style={{ color: governanceTokens.colorStatusWarning }}>{pendingCount}</Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Approved</Body2>
              <Title2 style={{ color: governanceTokens.colorStatusSuccess }}>{approvedCount}</Title2>
            </div>
            <div className={ps.statTile}>
              <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Rejected</Body2>
              <Title2 style={{ color: governanceTokens.colorStatusError }}>{rejectedCount}</Title2>
            </div>
          </div>
        </div>
      </Card>

      {/* Simulation Cards */}
      {SIMULATIONS.map((sim) => (
        <Card key={sim.id}>
          <div className={ps.cardBody}>
            <div className={ls.headerRow}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, flex: 1 }}>
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Badge appearance="tint" color="brand" icon={<BrainCircuitRegular />} size="small">
                    AI Recommendation
                  </Badge>
                  <Badge appearance="outline" size="small">{sim.sourceAgent}</Badge>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                    {new Date(sim.createdAt).toLocaleString()}
                  </Caption1>
                </div>
                <Title3>{sim.proposedPolicy}</Title3>
              </div>
              <Badge
                appearance="tint"
                color={statusColor(sim.status)}
                size="large"
              >
                {statusLabel(sim.status)}
              </Badge>
            </div>

            {/* Reasoning */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
              <Body2 style={{ fontWeight: 600 }}>Why this was suggested</Body2>
              <Body1 style={{ color: tokens.colorNeutralForeground2 }}>{sim.reasoning}</Body1>
            </div>

            {/* Impact Analysis */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}>
              <Body2 style={{ fontWeight: 600 }}>Impact Analysis</Body2>
              <div className={ls.impactGrid}>
                <div className={ls.impactTile}>
                  <CodeRegular style={{ fontSize: '20px', color: '#E852B9' }} />
                  <Title3 className={ps.metricValue}>{sim.impactAnalysis.affectedRepos}</Title3>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Repos</Caption1>
                </div>
                <div className={ls.impactTile}>
                  <PipelineRegular style={{ fontSize: '20px', color: '#E89652' }} />
                  <Title3 className={ps.metricValue}>{sim.impactAnalysis.affectedPipelines}</Title3>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Pipelines</Caption1>
                </div>
                <div className={ls.impactTile}>
                  <ServerRegular style={{ fontSize: '20px', color: '#13B5B1' }} />
                  <Title3 className={ps.metricValue}>{sim.impactAnalysis.affectedServices}</Title3>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Services</Caption1>
                </div>
                <div className={ls.impactTile}>
                  <WarningRegular style={{ fontSize: '20px', color: governanceTokens.colorStatusError }} />
                  <Title3 className={ps.metricValue} style={{ color: sim.predictedBlocks > 0 ? governanceTokens.colorStatusError : tokens.colorNeutralForeground1 }}>
                    {sim.predictedBlocks}
                  </Title3>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Predicted Blocks</Caption1>
                </div>
                <div className={ls.impactTile}>
                  <ShieldCheckmarkRegular style={{ fontSize: '20px', color: governanceTokens.colorStatusWarning }} />
                  <Title3 className={ps.metricValue} style={{ color: sim.predictedWarns > 0 ? governanceTokens.colorStatusWarning : tokens.colorNeutralForeground1 }}>
                    {sim.predictedWarns}
                  </Title3>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Predicted Warns</Caption1>
                </div>
                <div className={ls.impactTile}>
                  <Badge
                    appearance="tint"
                    color={falsePositiveColor(sim.falsePositiveRisk)}
                    size="small"
                  >
                    {sim.falsePositiveRisk}
                  </Badge>
                  <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>False Positive Risk</Caption1>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {sim.status === 'pending' && (
              <div className={ls.actionRow}>
                <Button
                  appearance="outline"
                  icon={<DismissRegular />}
                >
                  Reject
                </Button>
                <Button
                  appearance="primary"
                  icon={<CheckmarkRegular />}
                  style={{ backgroundColor: governanceTokens.colorStatusSuccess }}
                >
                  Approve Simulation
                </Button>
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
};
